import { useState } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { QuickscanData, QuickscanResults } from '@/types/quickscan';
import {
  QUICKSCAN_PRIORITY_SCALE,
  orderQuickscanPriorities,
  quickscanPriorityPercentage,
} from '@/lib/quickscan-priorities';

interface ContactInfo {
  email?: string;
  companyName?: string;
}

export const useProfessionalPDFGeneration = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);

  const createProfessionalTemplate = (
    quickscanData: QuickscanData,
    results: QuickscanResults,
    contactInfo?: ContactInfo,
    submissionId?: string
  ): string => {
    const formatCurrency = (amount: number) => {
      return new Intl.NumberFormat('nl-NL', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount);
    };

    const currentDate = new Date().toLocaleDateString('nl-NL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    // Get score color based on energy potential score
    const getScoreColor = (score: number) => {
      if (score >= 80) return '#22c55e'; // Green
      if (score >= 60) return '#0ea5e9'; // Blue  
      if (score >= 40) return '#f59e0b'; // Orange
      return '#ef4444'; // Red
    };

    const getScoreDescription = (score: number) => {
      if (score >= 80) return 'Uitstekende';
      if (score >= 60) return 'Goede';
      if (score >= 40) return 'Redelijke';
      return 'Basis';
    };

    // Create energy score circle SVG
    const createScoreCircle = (score: number) => {
      const color = getScoreColor(score);
      const circumference = 2 * Math.PI * 45;
      const strokeDasharray = circumference;
      const strokeDashoffset = circumference - (score / 100) * circumference;
      
      return `
        <svg width="140" height="140" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="45" fill="none" stroke="#e5e7eb" stroke-width="8"/>
          <circle cx="50" cy="50" r="45" fill="none" stroke="${color}" stroke-width="8" 
                  stroke-linecap="round" stroke-dasharray="${strokeDasharray}" 
                  stroke-dashoffset="${strokeDashoffset}" 
                  style="transition: stroke-dashoffset 0.5s ease-in-out; transform: rotate(-90deg); transform-origin: 50% 50%;"/>
        </svg>
      `;
    };

    // Format expansion plan details
    const formatExpansionDetails = () => {
      const details = quickscanData.expansionDetails;
      if (!details || (!details.location && !details.sustainability && !details.growth && !details.equipment)) {
        return '<p style="color: #6b7280; font-style: italic;">Geen uitbreidingsplannen gespecificeerd</p>';
      }

      let content = '';

      if (details.location) {
        content += `<div style="background: white; border-radius: 8px; padding: 16px; margin-bottom: 12px; border-left: 4px solid #0ea5e9;">
          <h4 style="color: #014336; margin-bottom: 12px; font-weight: 600;">🏢 Nieuwe Locatie</h4>
          <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px;">
            <div><strong>Locatie:</strong> ${details.location.location || 'Niet opgegeven'}</div>
            <div><strong>Eigendom:</strong> ${details.location.ownership === 'buy' ? 'Koop' : 'Huur'}</div>
            <div><strong>Oppervlakte:</strong> ${details.location.surfaceArea || 'Niet opgegeven'} m²</div>
            <div><strong>Doel:</strong> ${details.location.purpose || 'Niet opgegeven'}</div>
          </div>
        </div>`;
      }

      if (details.sustainability) {
        content += `<div style="background: white; border-radius: 8px; padding: 16px; margin-bottom: 12px; border-left: 4px solid #22c55e;">
          <h4 style="color: #014336; margin-bottom: 12px; font-weight: 600;">🌱 Duurzaamheidsplannen</h4>
          <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px;">
            <div><strong>Certificeringen:</strong> ${details.sustainability.certifications?.join(', ') || 'Geen'}</div>
            <div><strong>Doelen:</strong> ${details.sustainability.targets?.join(', ') || 'Geen'}</div>
            <div><strong>Tijdlijn:</strong> ${details.sustainability.timeline || 'Niet opgegeven'} jaar</div>
            <div><strong>CO2-reductie:</strong> ${details.sustainability.co2ReductionTarget || 'Niet opgegeven'}%</div>
            <div><strong>Budget:</strong> ${details.sustainability.budget || 'Niet opgegeven'}</div>
          </div>
        </div>`;
      }

      if (details.growth) {
        content += `<div style="background: white; border-radius: 8px; padding: 16px; margin-bottom: 12px; border-left: 4px solid #f59e0b;">
          <h4 style="color: #014336; margin-bottom: 12px; font-weight: 600;">📈 Bedrijfsgroei</h4>
          <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px;">
            <div><strong>Groeipercentage:</strong> ${details.growth.growthPercentage || 'Niet opgegeven'}%</div>
            <div><strong>Tijdsframe:</strong> ${details.growth.timeframe || 'Niet opgegeven'}</div>
            <div><strong>Groeitypes:</strong> ${details.growth.growthTypes?.join(', ') || 'Geen'}</div>
            <div><strong>Extra werknemers:</strong> ${details.growth.additionalEmployees || 'Niet opgegeven'}</div>
          </div>
        </div>`;
      }

      if (details.equipment) {
        content += `<div style="background: white; border-radius: 8px; padding: 16px; margin-bottom: 12px; border-left: 4px solid #8b5cf6;">
          <h4 style="color: #014336; margin-bottom: 12px; font-weight: 600;">⚡ Nieuwe Apparatuur</h4>
          <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px;">
            <div><strong>Apparaattype:</strong> ${details.equipment.equipmentType || details.equipment.equipmentTypes?.join(', ') || 'Niet opgegeven'}</div>
            <div><strong>Energieverbruik:</strong> ${details.equipment.energyConsumption || details.equipment.powerConsumption || 'Niet opgegeven'} kW</div>
            <div><strong>Aantal:</strong> ${details.equipment.quantity || 'Niet opgegeven'}</div>
            <div><strong>Bedrijfsuren:</strong> ${details.equipment.operatingHours || 'Niet opgegeven'} uur/dag</div>
            <div><strong>Tijdlijn:</strong> ${details.equipment.timeline || details.equipment.implementationTiming || 'Niet opgegeven'}</div>
          </div>
        </div>`;
      }

      return content || '<p style="color: #6b7280; font-style: italic;">Geen uitbreidingsplannen gespecificeerd</p>';
    };

    // Format priorities
    // De labels komen uit de gedeelde bron. Ze stonden hier ooit los, met
    // sleutels (costSavings, sustainability, futureProofing) die het formulier
    // nooit heeft weggeschreven — daardoor drukte de klant-PDF letterlijk
    // "cost-savings" en "co2-reduction" af zodra dit blok in beeld kwam.
    const formatPriorities = () => {
      const priorities = orderQuickscanPriorities(quickscanData.priorities);
      if (priorities.length === 0) {
        return '<p style="color: #6b7280; font-style: italic;">Geen prioriteiten ingesteld</p>';
      }

      return priorities
        .map(({ label, value }) => {
          const percentage = quickscanPriorityPercentage(value);
          const color = getScoreColor(percentage);

          return `
            <div style="margin-bottom: 16px;">
              <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                <span style="font-weight: 500;">${label}</span>
                <span style="color: #6b7280;">${value}/${QUICKSCAN_PRIORITY_SCALE.max}</span>
              </div>
              <div style="background: #e5e7eb; border-radius: 4px; height: 8px; overflow: hidden;">
                <div style="background: ${color}; height: 100%; width: ${percentage}%; transition: width 0.3s ease;"></div>
              </div>
            </div>
          `;
         }).join('');
    };

    // Use the actual Greenvision logo from the uploads folder
    const logoBase64 = `/lovable-uploads/9b92771c-e6b9-4d83-b9b2-1a3e2a08ce4d.png`;

    return `
<!DOCTYPE html>
<html lang="nl">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Energie Quickscan Rapport - ${contactInfo?.companyName || 'Uw Bedrijf'}</title>
    <style>
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
        
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
            line-height: 1.6;
            color: #1f2937;
            background: #ffffff;
            font-size: 14px;
        }
        
        .page {
            width: 210mm;
            height: 297mm;
            margin: 0 auto;
            background: white;
            padding: 20mm;
            position: relative;
            page-break-after: always;
        }
        
        .page:last-child {
            page-break-after: avoid;
        }
        
        .header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 30px;
            padding-bottom: 15px;
            border-bottom: 3px solid #014336;
        }
        
        .header-compact {
            margin-bottom: 20px;
            padding-bottom: 10px;
            border-bottom: 1px solid #e5e7eb;
        }
        
        .logo {
            height: 40px;
        }
        
        .header-info {
            text-align: right;
            color: #6b7280;
        }
        
        .header-info h1 {
            color: #014336;
            font-size: 24px;
            font-weight: 700;
            margin-bottom: 5px;
        }
        
        .company-section {
            background: linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%);
            border: 1px solid #22c55e20;
            border-radius: 12px;
            padding: 20px;
            margin-bottom: 25px;
        }
        
        .company-section h2 {
            color: #014336;
            font-size: 18px;
            font-weight: 600;
            margin-bottom: 16px;
            display: flex;
            align-items: center;
        }
        
        .company-section h2::before {
            content: "🏢";
            margin-right: 8px;
        }
        
        .company-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 12px;
        }
        
        .company-item {
            background: white;
            padding: 10px 14px;
            border-radius: 8px;
            border-left: 4px solid #014336;
        }
        
        .company-item strong {
            color: #374151;
            display: block;
            margin-bottom: 2px;
            font-weight: 500;
            font-size: 12px;
        }
        
        .company-item span {
            color: #6b7280;
            font-size: 11px;
        }
        
        .score-section {
            text-align: center;
            margin-bottom: 25px;
            padding: 25px;
            background: linear-gradient(135deg, #014336 0%, #0ea5e9 100%);
            border-radius: 16px;
            color: white;
        }
        
        .score-container {
            position: relative;
            display: inline-block;
            margin-bottom: 16px;
        }
        
        .score-number {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            font-size: 32px;
            font-weight: 700;
            color: #014336;
        }
        
        .score-label {
            font-size: 16px;
            font-weight: 600;
            margin-bottom: 8px;
        }
        
        .score-description {
            opacity: 0.9;
            font-size: 14px;
        }
        
        .metrics-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 15px;
            margin-bottom: 25px;
        }
        
        .metric-card {
            background: white;
            border: 2px solid #e5e7eb;
            border-radius: 12px;
            padding: 18px;
            text-align: center;
            position: relative;
            overflow: hidden;
        }
        
        .metric-card::before {
            content: "";
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 3px;
            background: linear-gradient(90deg, #014336, #0ea5e9);
        }
        
        .metric-icon {
            font-size: 24px;
            margin-bottom: 8px;
            display: block;
        }
        
        .metric-value {
            font-size: 18px;
            font-weight: 700;
            color: #014336;
            margin-bottom: 6px;
        }
        
        .metric-label {
            color: #6b7280;
            font-size: 11px;
            margin-bottom: 4px;
        }
        
        .metric-detail {
            color: #9ca3af;
            font-size: 10px;
        }
        
        .section {
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 8px;
            padding: 20px;
            margin-bottom: 20px;
        }
        
        .section h3 {
            color: #014336;
            font-size: 16px;
            font-weight: 600;
            margin-bottom: 16px;
            display: flex;
            align-items: center;
        }
        
        .tech-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 12px;
        }
        
        .tech-item {
            display: flex;
            justify-content: space-between;
            padding: 8px 0;
            border-bottom: 1px solid #e2e8f0;
            font-size: 12px;
        }
        
        .expansion-item {
            background: white;
            border-radius: 8px;
            padding: 16px;
            margin-bottom: 12px;
            border-left: 4px solid #0ea5e9;
        }
        
        .expansion-item h4 {
            color: #014336;
            margin-bottom: 12px;
            font-weight: 600;
            font-size: 14px;
        }
        
        .expansion-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 8px;
            font-size: 11px;
        }
        
        .priority-item {
            margin-bottom: 12px;
        }
        
        .priority-header {
            display: flex;
            justify-content: space-between;
            margin-bottom: 4px;
            font-size: 12px;
        }
        
        .priority-bar {
            background: #e5e7eb;
            border-radius: 4px;
            height: 6px;
            overflow: hidden;
        }
        
        .priority-fill {
            height: 100%;
            transition: width 0.3s ease;
        }
        
        .recommendation-item {
            background: white;
            border-radius: 8px;
            padding: 12px;
            margin-bottom: 10px;
            border-left: 4px solid #22c55e;
            display: flex;
            align-items: flex-start;
            font-size: 12px;
        }
        
        .recommendation-number {
            background: #014336;
            color: white;
            width: 20px;
            height: 20px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 10px;
            font-weight: 600;
            margin-right: 10px;
            flex-shrink: 0;
        }
        
        .contact-info {
            background: linear-gradient(135deg, #014336 0%, #0ea5e9 100%);
            color: white;
            border-radius: 12px;
            padding: 20px;
            text-align: center;
            margin-bottom: 20px;
        }
        
        .contact-info h3 {
            margin-bottom: 12px;
            font-size: 16px;
        }
        
        .footer {
            position: absolute;
            bottom: 15mm;
            left: 20mm;
            right: 20mm;
            text-align: center;
            padding-top: 12px;
            border-top: 1px solid #e5e7eb;
            color: #6b7280;
            font-size: 10px;
        }
        
        @media print {
            .page {
                margin: 0;
                box-shadow: none;
            }
            
            body {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
            }
        }
    </style>
</head>
<body>
    <!-- PAGINA 1: OVERZICHT & SCORE -->
    <div class="page">
        <div class="header">
            <img src="${logoBase64}" alt="Greenvision Energy" class="logo">
            <div class="header-info">
                <h1>Energie Quickscan</h1>
                <div>Rapport gegenereerd op ${currentDate}</div>
                ${submissionId ? `<div style="font-size: 10px; margin-top: 4px;">ID: ${submissionId}</div>` : ''}
            </div>
        </div>

        <div class="company-section">
            <h2>Bedrijfsoverzicht</h2>
            <div class="company-grid">
                <div class="company-item">
                    <strong>Bedrijfsnaam</strong>
                    <span>${contactInfo?.companyName || 'Niet opgegeven'}</span>
                </div>
                <div class="company-item">
                    <strong>Contactpersoon</strong>
                    <span>${contactInfo?.email || 'Niet opgegeven'}</span>
                </div>
                <div class="company-item">
                    <strong>Bedrijfstype</strong>
                    <span>${quickscanData.businessType || 'Niet opgegeven'}</span>
                </div>
                <div class="company-item">
                    <strong>Aantal werknemers</strong>
                    <span>${quickscanData.employeeCount || 'Niet opgegeven'}</span>
                </div>
                <div class="company-item">
                    <strong>Maandelijkse energiekosten</strong>
                    <span>${formatCurrency(quickscanData.monthlyEnergyCosts || 0)}</span>
                </div>
                <div class="company-item">
                    <strong>Piekuren energiegebruik</strong>
                    <span>${quickscanData.energyPeakHours?.map(h => `${h}:00`).join(', ') || 'Niet gespecificeerd'}</span>
                </div>
            </div>
        </div>

        <div class="score-section">
            <div class="score-container">
                ${createScoreCircle(results.energyPotentialScore || 0)}
                <div class="score-number">${results.energyPotentialScore || 0}</div>
            </div>
            <div class="score-label">Energie Potentieel Score</div>
            <div class="score-description">
                ${getScoreDescription(results.energyPotentialScore || 0)} 
                mogelijkheden voor energieoptimalisatie
            </div>
        </div>

        <div class="metrics-grid">
            <div class="metric-card">
                <div class="metric-icon">💰</div>
                <div class="metric-value">${formatCurrency(results.yearlyAnnualSavings || 0)}</div>
                <div class="metric-label">Jaarlijkse Besparing</div>
                <div class="metric-detail">≈ ${formatCurrency(Math.round((results.yearlyAnnualSavings || 0) / 12))} per maand</div>
            </div>
            <div class="metric-card">
                <div class="metric-icon">🌱</div>
                <div class="metric-value">${results.co2Reduction || 0} ton</div>
                <div class="metric-label">CO₂ Reductie per jaar</div>
                <div class="metric-detail">= ${Math.round((results.co2Reduction || 0) * 2.2)} bomen planten</div>
            </div>
            <div class="metric-card">
                <div class="metric-icon">📅</div>
                <div class="metric-value">${results.roiMonths || 0} maanden</div>
                <div class="metric-label">Terugverdientijd</div>
                <div class="metric-detail">ROI: ${Math.round(1200 / (results.roiMonths || 1))}% per jaar</div>
            </div>
        </div>

        <div class="footer">
            <div>Greenvision Energy - Energie Quickscan Rapport</div>
            <div style="margin-top: 4px;">Pagina 1 van 4</div>
        </div>
    </div>

    <!-- PAGINA 2: TECHNISCHE DETAILS -->
    <div class="page">
        <div class="header header-compact">
            <img src="${logoBase64}" alt="Greenvision Energy" class="logo">
            <div class="header-info">
                <h1>Technische Details</h1>
            </div>
        </div>

        <div class="section">
            <h3>⚡ Energiespecificaties</h3>
            <div class="tech-grid">
                <div class="tech-item">
                    <span>Geschat PV-opwekking:</span>
                    <strong>${quickscanData.estimatedPvGeneration || 0} kWh/jaar</strong>
                </div>
                <div class="tech-item">
                    <span>Geschat energieverbruik:</span>
                    <strong>${quickscanData.estimatedKwhUsage || 0} kWh/jaar</strong>
                </div>
                <div class="tech-item">
                    <span>Netcapaciteit levering:</span>
                    <strong>${quickscanData.connectionCapacityDelivery || 0} kW</strong>
                </div>
                <div class="tech-item">
                    <span>Netcapaciteit teruglevering:</span>
                    <strong>${quickscanData.connectionCapacityReturn || 0} kW</strong>
                </div>
                <div class="tech-item">
                    <span>Piekuren energiegebruik:</span>
                    <strong>${quickscanData.energyPeakHours?.map(h => `${h}:00`).join(', ') || 'Niet gespecificeerd'}</strong>
                </div>
                <div class="tech-item">
                    <span>Maandelijkse kosten:</span>
                    <strong>${formatCurrency(quickscanData.monthlyEnergyCosts || 0)}</strong>
                </div>
            </div>
        </div>

        <div class="section">
            <h3>🔧 Huidige Oplossingen</h3>
            <div style="background: white; padding: 16px; border-radius: 8px;">
                ${quickscanData.currentSolutions && quickscanData.currentSolutions.length > 0 
                  ? quickscanData.currentSolutions.map(solution => `
                    <div style="padding: 8px 0; border-bottom: 1px solid #e2e8f0; font-size: 12px;">
                      • ${solution}
                    </div>
                  `).join('')
                  : '<div style="color: #6b7280; font-style: italic; font-size: 12px;">Geen huidige oplossingen gespecificeerd</div>'
                }
            </div>
        </div>

        <div class="section">
            <h3>📊 Berekende Resultaten</h3>
            <div class="tech-grid">
                <div class="tech-item">
                    <span>Energiepotentieel score:</span>
                    <strong>${results.energyPotentialScore || 0}/100</strong>
                </div>
                <div class="tech-item">
                    <span>Jaarlijkse besparing:</span>
                    <strong>${formatCurrency(results.yearlyAnnualSavings || 0)}</strong>
                </div>
                <div class="tech-item">
                    <span>CO₂ reductie:</span>
                    <strong>${results.co2Reduction || 0} ton/jaar</strong>
                </div>
                <div class="tech-item">
                    <span>Terugverdientijd:</span>
                    <strong>${results.roiMonths || 0} maanden</strong>
                </div>
                <div class="tech-item">
                    <span>ROI percentage:</span>
                    <strong>${Math.round(1200 / (results.roiMonths || 1))}% per jaar</strong>
                </div>
                <div class="tech-item">
                    <span>Milieu-impact:</span>
                    <strong>${Math.round((results.co2Reduction || 0) * 2.2)} bomen equivalent</strong>
                </div>
            </div>
        </div>

        <div class="footer">
            <div>Greenvision Energy - Energie Quickscan Rapport</div>
            <div style="margin-top: 4px;">Pagina 2 van 4</div>
        </div>
    </div>

    <!-- PAGINA 3: UITBREIDINGSPLANNEN & PRIORITEITEN -->
    <div class="page">
        <div class="header header-compact">
            <img src="${logoBase64}" alt="Greenvision Energy" class="logo">
            <div class="header-info">
                <h1>Plannen & Prioriteiten</h1>
            </div>
        </div>

        <div class="section">
            <h3>🚀 Uitbreidingsplannen Komende 2 Jaar</h3>
            ${formatExpansionDetails()}
        </div>

        <div class="section">
            <h3>⚖️ Prioriteiten</h3>
            <div style="background: white; padding: 16px; border-radius: 8px;">
                ${formatPriorities()}
            </div>
        </div>

        <div class="footer">
            <div>Greenvision Energy - Energie Quickscan Rapport</div>
            <div style="margin-top: 4px;">Pagina 3 van 4</div>
        </div>
    </div>

    <!-- PAGINA 4: AANBEVELINGEN & CONTACT -->
    <div class="page">
        <div class="header header-compact">
            <img src="${logoBase64}" alt="Greenvision Energy" class="logo">
            <div class="header-info">
                <h1>Aanbevelingen & Contact</h1>
            </div>
        </div>

        <div class="section">
            <h3>💡 Aanbevelingen voor uw bedrijf</h3>
            <div style="background: white; padding: 16px; border-radius: 8px;">
                ${results.recommendations?.map((rec, index) => `
                    <div class="recommendation-item">
                        <div class="recommendation-number">${index + 1}</div>
                        <div>${rec}</div>
                    </div>
                `).join('') || '<div class="recommendation-item"><div class="recommendation-number">1</div><div>Geen specifieke aanbevelingen beschikbaar</div></div>'}
            </div>
        </div>

        <div class="contact-info">
            <h3>Volgende Stappen</h3>
            <p style="margin-bottom: 16px;">Geïnteresseerd in een uitgebreide analyse of offerte? Neem contact met ons op voor een persoonlijk adviesgesprek.</p>
            <div style="font-weight: 500; margin-bottom: 12px;">
                📧 info@greenvisionenergy.nl
            </div>
            <div style="font-weight: 500;">
                📞 +31 (0)20 123 4567
            </div>
        </div>

        <div style="background: #f8fafc; border-radius: 8px; padding: 20px; text-align: center; margin-top: 20px;">
            <h3 style="color: #014336; margin-bottom: 12px;">Waarom Greenvision Energy?</h3>
            <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; font-size: 12px;">
                <div>✅ 15+ jaar ervaring</div>
                <div>✅ Eigen installatieteam</div>
                <div>✅ Complete ontzorging</div>
                <div>✅ Maatwerk oplossingen</div>
            </div>
        </div>

        <div class="footer">
            <div>Dit rapport is gegenereerd op basis van de opgegeven gegevens tijdens de Energie Quickscan.</div>
            <div style="margin-top: 4px;">© ${new Date().getFullYear()} Greenvision Energy. Alle rechten voorbehouden. | Pagina 4 van 4</div>
        </div>
    </div>
</body>
</html>`;
  };

  const generateProfessionalPDF = async (
    quickscanData: QuickscanData,
    results: QuickscanResults,
    contactInfo?: ContactInfo,
    submissionId?: string
  ) => {
    setIsGenerating(true);
    setGenerationError(null);

    try {
      // Create the HTML template
      const htmlTemplate = createProfessionalTemplate(quickscanData, results, contactInfo, submissionId);
      
      // Create a temporary container for rendering
      const tempContainer = document.createElement('div');
      tempContainer.innerHTML = htmlTemplate;
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '-9999px';
      tempContainer.style.width = '210mm';
      document.body.appendChild(tempContainer);

      // Create PDF
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      // Get all pages
      const pages = tempContainer.querySelectorAll('.page');
      
      // Render each page
      for (let i = 0; i < pages.length; i++) {
        const page = pages[i] as HTMLElement;
        
        // Render page to canvas
        const canvas = await html2canvas(page, {
          width: 794, // A4 width in pixels at 96 DPI
          height: 1123, // A4 height in pixels at 96 DPI
          scale: 2, // Higher quality
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff'
        });
        
        const imgData = canvas.toDataURL('image/png');
        
        // Add new page if not the first one
        if (i > 0) {
          pdf.addPage();
        }
        
        // Add image to PDF
        pdf.addImage(imgData, 'PNG', 0, 0, 210, 297);
      }

      // Clean up
      document.body.removeChild(tempContainer);

      // Download the PDF
      pdf.save(`energie-quickscan-rapport-${contactInfo?.companyName || 'rapport'}-${submissionId || Date.now()}.pdf`);

      return true;
    } catch (error) {
      console.error('Professional PDF generation error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Er ging iets mis bij het genereren van de PDF';
      setGenerationError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsGenerating(false);
    }
  };

  return {
    generateProfessionalPDF,
    isGenerating,
    generationError
  };
};